<template>
  <div class="font-sans min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <div class="flex-grow pb-12 lg:pb-20">
      <!-- Hero Section -->
      <section
        class="relative bg-blue-50 dark:bg-gray-900 pt-6 pb-8 lg:pt-10 lg:pb-12 overflow-hidden border-b border-gray-100 dark:border-gray-700"
      >
        <!-- Watermark Background -->
        <div
          class="absolute inset-0 opacity-[0.03] pointer-events-none overflow-hidden flex flex-wrap content-start"
        >
          <svg
            v-for="i in 150"
            :key="i"
            class="w-16 h-16 text-[#293146] dark:text-gray-700 m-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            ></path>
          </svg>
        </div>

        <div class="relative max-w-screen-xl mx-auto px-4 z-10">
          <router-link
            to="/"
            class="inline-flex items-center text-sm font-semibold text-[#293146] dark:text-gray-200 hover:text-[#C5B47A] dark:hover:text-[#C5B47A] mb-6 transition-colors group"
          >
            <svg
              class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
            Kembali ke Lowongan
          </router-link>

          <div
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none border border-gray-100 dark:border-gray-700 p-5 md:p-10 relative z-20"
          >
            <div class="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div
                class="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-2xl flex items-center justify-center text-green-600 dark:text-green-300 shrink-0"
              >
                <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  ></path>
                </svg>
              </div>
              <div>
                <h1 class="text-2xl md:text-4xl font-black text-[#293146] dark:text-white mb-2">
                  Kontak Kami
                </h1>
                <p class="text-sm md:text-base text-gray-500 dark:text-gray-300 font-medium">
                  Butuh bantuan? Tim kami siap melayani Anda
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Loading State -->
      <div v-if="isLoading" class="max-w-4xl mx-auto px-4 mt-10 flex justify-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#293146] dark:border-[#C5B47A]"
        ></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="max-w-4xl mx-auto px-4 mt-10 text-center py-20">
        <p class="text-red-500">Gagal memuat data kontak</p>
      </div>

      <!-- Content -->
      <main v-else-if="contact" class="max-w-4xl mx-auto px-4 mt-10">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- WhatsApp Contact 1 - Admin -->
          <a
            :href="`https://wa.me/${contact.admin.phone.replace(/[^0-9]/g, '')}`"
            target="_blank"
            class="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-none hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-gray-900/50 transition-all duration-300 group"
          >
            <div class="flex items-center justify-between mb-6">
              <div
                class="w-14 h-14 bg-green-50 dark:bg-green-900 rounded-2xl flex items-center justify-center text-green-600 dark:text-green-300 group-hover:scale-110 transition-transform"
              >
                <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.483 8.413-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.308 1.654zm6.233-4.087l.446.265c1.512.9 3.303 1.374 5.14 1.375 5.565 0 10.088-4.523 10.091-10.088.002-2.697-1.047-5.232-2.956-7.141-1.909-1.908-4.44-2.956-7.142-2.957-5.564 0-10.088 4.523-10.091 10.088-.001 1.89.504 3.734 1.462 5.334l.32.533-1.056 3.86 3.951-1.036zm10.654-7.422c-.22-.11-1.3-.642-1.49-.113-.19-.072-.328-.11-.478-.328-.15-.22-.58-1.203-.71-1.472-.13-.27-.27-.27-.478-.28-.11 0-.328-.022-.529-.022s-.528.077-.805.373c-.276.299-1.056 1.033-1.056 2.517s1.08 2.914 1.23 3.111c.15.197 2.126 3.246 5.15 4.554.72.311 1.282.497 1.72.637.722.222 1.38.191 1.9.113.58-.087 1.3-.531 1.485-1.043.186-.511.186-.949.13-1.043-.056-.094-.206-.15-.426-.26z"
                  />
                </svg>
              </div>
              <span
                class="text-[10px] font-black tracking-widest text-green-600 dark:text-green-300 uppercase bg-green-50 dark:bg-green-900 px-3 py-1 rounded-full"
                >{{ contact.admin.type }}</span
              >
            </div>
            <h3 class="text-xl font-black text-[#293146] dark:text-white mb-1">
              {{ contact.admin.name }}
            </h3>
            <p class="text-gray-400 dark:text-gray-500 text-sm mb-4">
              {{ contact.admin.description }}
            </p>
            <p
              class="text-2xl font-black text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors"
            >
              {{ formatPhone(contact.admin.phone) }}
            </p>
          </a>

          <!-- WhatsApp Contact 2 - Support -->
          <a
            :href="`https://wa.me/${contact.support.phone.replace(/[^0-9]/g, '')}`"
            target="_blank"
            class="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-none hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-gray-900/50 transition-all duration-300 group"
          >
            <div class="flex items-center justify-between mb-6">
              <div
                class="w-14 h-14 bg-blue-50 dark:bg-blue-900 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-300 group-hover:scale-110 transition-transform"
              >
                <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.246 2.248 3.484 5.232 3.483 8.413-.003 6.557-5.338 11.892-11.893 11.892-1.997-.001-3.951-.5-5.688-1.448l-6.308 1.654zm6.233-4.087l.446.265c1.512.9 3.303 1.374 5.14 1.375 5.565 0 10.088-4.523 10.091-10.088.002-2.697-1.047-5.232-2.956-7.141-1.909-1.908-4.44-2.956-7.142-2.957-5.564 0-10.088 4.523-10.091 10.088-.001 1.89.504 3.734 1.462 5.334l.32.533-1.056 3.86 3.951-1.036zm10.654-7.422c-.22-.11-1.3-.642-1.49-.113-.19-.072-.328-.11-.478-.328-.15-.22-.58-1.203-.71-1.472-.13-.27-.27-.27-.478-.28-.11 0-.328-.022-.529-.022s-.528.077-.805.373c-.276.299-1.056 1.033-1.056 2.517s1.08 2.914 1.23 3.111c.15.197 2.126 3.246 5.15 4.554.72.311 1.282.497 1.72.637.722.222 1.38.191 1.9.113.58-.087 1.3-.531 1.485-1.043.186-.511.186-.949.13-1.043-.056-.094-.206-.15-.426-.26z"
                  />
                </svg>
              </div>
              <span
                class="text-[10px] font-black tracking-widest text-blue-600 dark:text-blue-300 uppercase bg-blue-50 dark:bg-blue-900 px-3 py-1 rounded-full"
                >{{ contact.support.type }}</span
              >
            </div>
            <h3 class="text-xl font-black text-[#293146] dark:text-white mb-1">
              {{ contact.support.name }}
            </h3>
            <p class="text-gray-400 dark:text-gray-500 text-sm mb-4">
              {{ contact.support.description }}
            </p>
            <p
              class="text-2xl font-black text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
            >
              {{ formatPhone(contact.support.phone) }}
            </p>
          </a>
        </div>

        <div
          class="mt-12 bg-white dark:bg-gray-800 rounded-3xl p-6 md:p-12 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-none"
        >
          <div class="flex flex-col md:flex-row items-center gap-10">
            <div class="flex-1 space-y-4 text-center md:text-left">
              <h2 class="text-2xl font-black text-[#293146] dark:text-white">
                Kunjungi Kantor Kami
              </h2>
              <p class="text-sm md:text-base text-gray-500 dark:text-gray-300 leading-relaxed">
                Kami berlokasi di Jambi. Anda dapat menjadwalkan pertemuan untuk konsultasi bisnis
                lebih lanjut.
              </p>
              <div class="space-y-3 inline-block md:block text-left">
                <div class="flex items-start">
                  <svg
                    class="w-5 h-5 mr-3 text-red-500 dark:text-red-400 shrink-0 mt-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                  <span class="text-gray-700 dark:text-gray-200 font-medium text-sm md:text-base">{{
                    formatAddress(contact.address)
                  }}</span>
                </div>
                <div class="flex items-start">
                  <svg
                    class="w-5 h-5 mr-3 text-[#293146] dark:text-[#C5B47A] shrink-0 mt-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                  <span class="text-gray-700 dark:text-gray-200 font-medium text-sm md:text-base">{{
                    contact.email
                  }}</span>
                </div>
              </div>
            </div>
            <div class="w-full md:w-1/3">
              <div
                class="aspect-square bg-gray-50 dark:bg-gray-700 rounded-3xl flex items-center justify-center border border-gray-100 dark:border-gray-600 p-8 shadow-inner"
              >
                <svg
                  class="w-20 h-20 md:w-24 md:h-24 text-gray-200 dark:text-gray-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                  ></path>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    <AppFooter />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { initFlowbite } from 'flowbite';
import AppFooter from '../components/layout/AppFooter.vue';
import { useContact } from '../composables/useContact';
import { useSeoMeta } from '../composables/useSeoMeta';

useSeoMeta({
  title: 'Kontak Kami',
  description:
    'Hubungi tim Lokerjambi.co.id melalui WhatsApp untuk bantuan pasang lowongan, konsultasi rekrutmen, atau pertanyaan seputar layanan kami. Tim kami siap melayani Anda.',
  path: '/contact',
});

const { contact, isLoading, error, fetchContact } = useContact();

const formatPhone = (phone) => {
  return phone.replace(/(\d{4})(\d{4})(\d{4})/, '$1 $2 $3');
};

const formatAddress = (address) => {
  if (!address) return '-';
  return `${address.street || ''}, ${address.village || ''}, ${address.district || ''}, ${address.city || ''}, ${address.province || ''}, ${address.country || ''}`.replace(
    /, $/,
    ''
  );
};

onMounted(async () => {
  initFlowbite();
  await fetchContact();
});
</script>
